import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// ---------------- Session handling ----------------
// The access token lives ONLY in an HttpOnly cookie. It is never written to
// localStorage and never placed in a URL, so injected JavaScript, browser
// history, referrer headers and analytics cannot harvest a reusable
// credential. `withCredentials` is what authenticates every request.
function readCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

// Double-submit CSRF: the server sets a readable `csrf_token` cookie at login
// and requires the same value back in this header on every unsafe request.
api.interceptors.request.use((cfg) => {
  const method = (cfg.method || "get").toUpperCase();
  if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
    const csrf = readCookie("csrf_token");
    if (csrf) cfg.headers["X-CSRF-Token"] = csrf;
  }
  return cfg;
});

// ---------------- Silent refresh ----------------
// The access JWT is short-lived (24h). On the first 401 we call /auth/refresh
// (which rotates the cookie) and replay the original request once.
const NO_REFRESH_PATHS = ["/auth/login", "/auth/register", "/auth/refresh",
                          "/auth/session", "/auth/google/exchange",
                          "/auth/mobile/verify-otp", "/auth/me"];

let refreshPromise = null;
async function doRefresh() {
  if (!refreshPromise) {
    const csrf = readCookie("csrf_token");
    refreshPromise = axios
      .post(`${API}/auth/refresh`, {}, {
        withCredentials: true,
        headers: csrf ? { "X-CSRF-Token": csrf } : {},
      })
      .then((r) => r.data)
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const orig = error.config || {};
    const status = error.response?.status;
    const url = orig.url || "";
    if (
      status === 401 &&
      !orig._retried &&
      !NO_REFRESH_PATHS.some((p) => url.includes(p))
    ) {
      orig._retried = true;
      try {
        await doRefresh();
        return api(orig);
      } catch (_e) {
        clearRefreshTimer();
      }
    }
    return Promise.reject(error);
  }
);

let refreshTimer = null;
export function scheduleTokenRefresh() {
  clearRefreshTimer();
  // Fire ~1h before the 24h cookie expires. If the tab is closed we simply
  // rely on the 401-driven refresh path above.
  refreshTimer = setTimeout(() => {
    doRefresh()
      .then(() => scheduleTokenRefresh())
      .catch(() => {}); // silent — 401 interceptor will handle next request
  }, 23 * 60 * 60 * 1000);
}
export function clearRefreshTimer() {
  if (refreshTimer) { clearTimeout(refreshTimer); refreshTimer = null; }
}

/** Download an authenticated file as a blob and hand back an object URL.
 *  Used for KYC documents, which must never be linked with a token in the URL. */
export async function fetchAuthedBlobUrl(path) {
  const { data } = await api.get(path, { responseType: "blob" });
  return URL.createObjectURL(data);
}

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong";
  // Pydantic prefixes custom validator messages with "Value error, " — strip
  // it so users see the message we actually wrote.
  const clean = (m) => m.replace(/^Value error,\s*/i, "");
  if (typeof detail === "string") return clean(detail);
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? clean(e.msg) : JSON.stringify(e))).join(" ");
  if (detail?.msg) return clean(detail.msg);
  return String(detail);
}
